import React, { memo, useMemo, useState, useEffect, useCallback } from 'react'
import { Steps } from 'intro.js-react'
import { UserPlus, Settings, Filter, HelpCircle } from 'lucide-react'
import { NodeProps, useReactFlow } from '@xyflow/react'
import { Button } from '@/components/ui/button'
import useDataSettings from '@/hooks/useDataSettings'
import useIntroTour from '@/hooks/useIntroTour'
import NodeBaseContainer from '@/components/nodes/base/base-container/NodeBaseContainer'
import NodeConfigurationDialog from '@/components/dialogs/configuration-dialog/NodeConfigurationDialog'
import NodeDataSettingsDialog from '@/components/dialogs/data-settings-dialog/NodeDataSettingsDialog'
import { useQuickStartProcessing } from './hooks'
import { QuickStartNodeData } from './types'
import styles from '@/styles/NodeQuickStart.module.css'
import '@/styles/introjs.css'

const QuickStartNode: React.FC<NodeProps & { data: QuickStartNodeData }> = ({
  id,
  data,
  selected,
}) => {
  const { updateNodeData } = useReactFlow()

  // Direct state management like working nodes
  const [localChallenge, setLocalChallenge] = useState(data.challenge || '')
  const [localDesiredOutcome, setLocalDesiredOutcome] = useState(data.desiredOutcome || '')
  const [isConfigDialogVisible, setIsConfigDialogVisible] = useState(false)
  const [nodeConfig, setNodeConfig] = useState(data.configuration || {})
  const [showDebugInfo] = useState(false)

  // Sync local state from data prop
  useEffect(() => {
    setLocalChallenge(data.challenge || '')
  }, [data.challenge])

  useEffect(() => {
    setLocalDesiredOutcome(data.desiredOutcome || '')
  }, [data.desiredOutcome])

  useEffect(() => {
    setNodeConfig(data.configuration || {})
  }, [data.configuration])
  // Direct syncNodeData like working nodes - removed data from deps to prevent loops
  const syncNodeData = useCallback((updates: Partial<QuickStartNodeData>) => {
    updateNodeData(id, updates)
  }, [id, updateNodeData])
  // Derived state
  const isLocked = data.isLocked || false
  const isMinimized = data.isMinimized || false  // Initialize minimize level from data - CRITICAL FIX
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { minimizeLevel: 'normal' })
    }
  }, [data.minimizeLevel, id, updateNodeData])

  // Validation
  const validation = {
    fieldErrors: {
      challenge: !localChallenge.trim() ? 'Challenge is required' : '',
      desiredOutcome: !localDesiredOutcome.trim() ? 'Desired outcome is required' : '',
    }
  }

  // Handlers
  const handleLock = useCallback(() => {
    syncNodeData({ isLocked: !isLocked })
  }, [syncNodeData, isLocked])

  const handleMinimize = useCallback(() => {
    const newMinState = !isMinimized
    syncNodeData({
      isMinimized: newMinState,
      minimizeLevel: newMinState ? 'medium' : 'normal'
    })
  }, [syncNodeData, isMinimized])

  const handleTitleChange = useCallback((title: string) => {
    syncNodeData({ label: title })
  }, [syncNodeData])

  const handleConfigClick = useCallback(() => {
    setIsConfigDialogVisible(true)
  }, [])
  const handleAssignUsersClick = useCallback(() => {
    const emailsStr = window.prompt(
      'Enter emails separated by space',
      Array.isArray(data.assignedEmails) ? data.assignedEmails.join(' ') : (data.assignedEmails || '')
    )
    if (emailsStr !== null) {
      // QuickStartNodeData expects assignedEmails to be a string, not string[]
      syncNodeData({ assignedEmails: emailsStr })
    }
  }, [data.assignedEmails, syncNodeData])

  const handleChallengeBlur = useCallback(() => {
    if (localChallenge !== data.challenge) {
      syncNodeData({ challenge: localChallenge })
    }
  }, [localChallenge, data.challenge, syncNodeData])

  const handleDesiredOutcomeBlur = useCallback(() => {
    if (localDesiredOutcome !== data.desiredOutcome) {
      syncNodeData({ desiredOutcome: localDesiredOutcome })
    }
  }, [localDesiredOutcome, data.desiredOutcome, syncNodeData])

  const handleMinimizeLevelChange = useCallback((level: 'normal' | 'medium' | 'minimum') => {
    syncNodeData({
      minimizeLevel: level,
      isMinimized: level !== 'normal'
    })
  }, [syncNodeData])

  // Processing logic
  const processing = useQuickStartProcessing(id, data, syncNodeData)
  const { handleProcessClick, canProcess } = processing

  // Intro tour
  const { introEnabled, setIntroEnabled, introSteps, introOptions } =
    useIntroTour(id, null)

  // Data settings dialog state
  const [localDataSettingsDialogVisible, setLocalDataSettingsDialogVisible] =
    useState(false)

  // Data settings management
  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleRemoveInputLink,
    handleSyncInputLink,
    handleOpenDataSettingsDialog,
  } = useDataSettings(
    id,
    data,
    syncNodeData,
    showDebugInfo,
    '',
    new Map(),
    '',
    setLocalDataSettingsDialogVisible
  )

  // Dark mode detection
  const [isDarkMode, setIsDarkMode] = useState(false)
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(
        document.documentElement.getAttribute('data-theme') === 'dark'
      )
    }
    checkDarkMode()
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })
    return () => observer.disconnect()
  }, [])
  // Helper to prepare exposedData fields for QuickStartNode
  const prepareExposedDataFields = () => ({
    challenge: {
      value: data.challenge || '',
      enabled: true,
      label: 'Challenge',
      description: 'The challenge that needs to be addressed.',
      permissions: { readable: true, writable: false },
    },
    desiredOutcome: {
      value: data.desiredOutcome || '',
      enabled: true,
      label: 'Desired Outcome',
      description: 'The outcome as envisioned to resolve the challenge.',
      permissions: { readable: true, writable: false },
    },
    linkedKnowledge: {
      value: data.linkedKnowledge || '',
      enabled: true,
      label: 'Linked Knowledge',
      description: 'Knowledge imported from linked nodes.',
      permissions: { readable: true, writable: false },
    },
  })  // Keep exposedData in sync with challenge/desiredOutcome/linkedKnowledge
  useEffect(() => {
    const newExposed = prepareExposedDataFields()

    setDataSettings((prev) => ({
      ...prev,
      exposedData: newExposed,
    }))
    // Sync exposed data to node data so other nodes can see it
    updateNodeData(id, {
      dataSettings: {
        inputLinks: data.dataSettings?.inputLinks || [],
        outputLinks: data.dataSettings?.outputLinks || [],
        availableNodes: data.dataSettings?.availableNodes || [],
        exposedData: newExposed,
      },
    })
  }, [data.challenge, data.desiredOutcome, data.linkedKnowledge, id, updateNodeData, data.dataSettings?.inputLinks, data.dataSettings?.outputLinks, data.dataSettings?.availableNodes])
  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {}

    if (data.challenge?.trim()) {
      minifiedInfo.challenge = data.challenge
    }

    if (data.desiredOutcome?.trim()) {
      minifiedInfo['desired outcome'] = data.desiredOutcome
    }

    if (data.linkedKnowledge?.trim()) {
      minifiedInfo['linked knowledge'] = data.linkedKnowledge
    }

    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {}
    const hasChanges =
      JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo)

    if (hasChanges) {
      updateNodeData(id, { minifiedInfo })
    }
  }, [
    data.challenge,
    data.desiredOutcome,
    data.linkedKnowledge,
    data.minifiedInfo,
    id,
    updateNodeData,
  ])

  // Custom buttons configuration - memoized to prevent re-renders
  const customButtons = useMemo(() => [
    {
      id: '1',
      onClick: handleAssignUsersClick,
      icon: <UserPlus size={16} />,
      tooltip: 'Assign user(s)',
    },
    {
      id: '2',
      onClick: handleConfigClick,
      icon: <Settings size={16} />,
      tooltip: 'Settings',
    },
    {
      id: '3',
      onClick: () => setIntroEnabled(true),
      icon: <HelpCircle size={16} />,
      tooltip: 'Help & Guide',
    },
  ], [handleAssignUsersClick, handleConfigClick, setIntroEnabled])

  const filteredCustomButtons = useMemo(() => {
    const filtered = customButtons.filter(
      (button) => !['1', '2'].includes(button.id)
    )
    return [
      ...filtered,
      {
        id: '5',
        onClick: handleOpenDataSettingsDialog,
        icon: <Filter size={16} />,
        tooltip: 'Data connection settings',
      },
    ]
  }, [customButtons, handleOpenDataSettingsDialog])

  // Compute status for NodeBaseContainer using useMemo
  const nodeStatus = useMemo(() => {
    if (data.documentRequest?.['sysagent-cmd']?.includes('Started'))
      return 'loading'
    if (data.statusMessage && data.statusMessage.toLowerCase().includes('error'))
      return 'error'
    if (data.statusMessage && data.statusMessage.toLowerCase().includes('complete'))
      return 'success'
    return 'initial'
  }, [data.documentRequest?.['sysagent-cmd'], data.statusMessage])

  return (<NodeBaseContainer
    id={id}
    data={data}
    resizable={true}
    nodeTitle={data.label || 'Quick Start'}
    isLocked={isLocked}
    isMinimized={data.minimizeLevel !== 'normal'}
    minimizeLevel={data.minimizeLevel || 'normal'}
    onLockChange={handleLock}
    onMinimizeChange={handleMinimize}
    onMinimizeLevelChange={handleMinimizeLevelChange}
    onTitleChange={handleTitleChange}
    customButtons={filteredCustomButtons}
    assignedEmails={
      Array.isArray(data.assignedEmails) ? data.assignedEmails :
        (data.assignedEmails ? data.assignedEmails.split(/\s+/).filter(Boolean) : [])
    }
    onAssignUsersClick={handleAssignUsersClick}
    onConfigClick={handleConfigClick}
    dataTour="quickstart-node"
    status={nodeStatus}
    selected={selected}
  >      {/* Always render the Guided UI (no levelOfUsage switching) */}
    {data.minimizeLevel === 'normal' && (
      <div
        className={`${styles.nodeContent} quickstart-input flex flex-col h-full`}
      >
        {/* Top section: Challenge and Desired Outcome textareas - takes available space */}          <div
          className={`${styles.textAreaContainer} flex-grow flex flex-col gap-2 min-h-0`}
          data-tour="quickstart-form"
        >            <textarea
            placeholder="Challenge: Describe the challenge."
            value={localChallenge}
            onChange={(e) => setLocalChallenge(e.target.value)}
            onBlur={handleChallengeBlur}
            disabled={isLocked} className={`${styles.textArea} nodrag nowheel flex-grow min-h-0 challenge ${validation.fieldErrors.challenge ? 'border-red-500' : ''
              }`}
            data-tour="challenge-field"
          />
          {validation.fieldErrors.challenge && (
            <div className="text-xs text-red-500">
              {validation.fieldErrors.challenge}
            </div>
          )}
          <div className="text-xs text-muted-foreground mb-2">
            Note: URLs entered here are not processed or followed; they are treated as plain text only.
          </div>            <textarea
            placeholder="Desired Outcome: Describe the outcome."
            value={localDesiredOutcome}
            onChange={(e) => setLocalDesiredOutcome(e.target.value)}
            onBlur={handleDesiredOutcomeBlur}
            disabled={isLocked}
            className={`${styles.textArea} nodrag nowheel flex-grow min-h-0 desiredOutcome ${validation.fieldErrors.desiredOutcome ? 'border-red-500' : ''
              }`}
            data-tour="desired-outcome-field"
          />
          {validation.fieldErrors.desiredOutcome && (
            <div className="text-xs text-red-500">
              {validation.fieldErrors.desiredOutcome}
            </div>
          )}
          <div className="text-xs text-muted-foreground mb-2">
            Note: URLs entered into challenge or desired outcome are not processed or followed; they are treated as plain text only.
          </div>
        </div>

        {/* Bottom section: Proceed button and status message - fixed at bottom */}
        <div className="flex flex-col gap-2 flex-shrink-0 mt-2">
          {!isLocked && (<Button
            className={`${styles.processButton} quickstart-proceed-button`}
            onClick={handleProcessClick}
            variant={isDarkMode ? 'outline' : 'default'}
            disabled={!canProcess}
            data-tour="proceed-button"
          >
            Proceed
          </Button>
          )}
          {data.statusMessage && (
            <div className={styles.statusLabel}>{data.statusMessage}</div>
          )}
        </div>
      </div>
    )}

    {isConfigDialogVisible && !isLocked && (
      <NodeConfigurationDialog
        visible={isConfigDialogVisible}
        configuration={nodeConfig as any}
        onSave={(newConfig) => {
          syncNodeData({ configuration: newConfig })
          setIsConfigDialogVisible(false)
        }}
        onHide={() => setIsConfigDialogVisible(false)}
      />
    )}

    <Steps
      enabled={introEnabled}
      steps={introSteps}
      initialStep={0}
      onExit={() => setIntroEnabled(false)}
      options={introOptions}
    />

    {localDataSettingsDialogVisible && (
      <NodeDataSettingsDialog
        visible={localDataSettingsDialogVisible}
        onHide={() => setLocalDataSettingsDialogVisible(false)}
        settings={dataSettings}
        nodeId={id}
        onSettingsChange={handleDataSettingsChange}
        onRemoveInputLink={handleRemoveInputLink}
        onSyncInputLink={handleSyncInputLink}
        forceShowAvailableNodes={true}
        debug={showDebugInfo}
      />
    )}

    {dataSettings.inputLinks.length > 0 && (
      <div
        className={`${styles.linkedKnowledge} mt-4 p-2 border rounded-md bg-gray-50`}
      >
        <h3 className="font-medium mb-2">Linked Knowledge:</h3>
        <div className="text-sm whitespace-pre-wrap">
          {data.linkedKnowledge}
        </div>
      </div>
    )}
  </NodeBaseContainer>
  )
}

export default memo(QuickStartNode)
